// pages/AirlineContacts.jsx
import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import ReviewsBar from "../components/ReviewsBar";
import api from "../utils/api";
import "../global.css";
import "../styles/faqPage.css"; // → use the same clean grid layout

// Clean airline name if API returns BOM/Unicode artefacts
const cleanAirlineName = (str) =>
  (str || "")
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .replace(/[^\x20-\x7E]/g, "")
    .trim();

export default function AirlineContacts() {
  const [query, setQuery] = useState("");
  const [airline, setAirline] = useState(null);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);

  const search = async () => {
    setSearched(true);
    setAirline(null);

    if (query.trim().length < 3) return;

    setLoading(true);

    try {
      // 1) Contact info
      const contactRes = await api.get(
        `/api/airlines/contact?airline=${encodeURIComponent(query)}`
      );

      const contactData = contactRes.data.contact || null;

      // 2) Clean name + IATA code lookup
      const routeRes = await api.get(
        `/api/airlines/routes?airline=${encodeURIComponent(query)}`
      );

      let cleanName = null;
      let cleanCode = null;

      if (Array.isArray(routeRes.data.routes) && routeRes.data.routes.length > 0) {
        cleanName = routeRes.data.routes[0].airline;
        cleanCode = routeRes.data.routes[0].code;
      }

      // 3) Merge datasets
      if (contactData) {
        setAirline({
          ...contactData,
          airline: cleanName || contactData.airline,
          iata_code: cleanCode || contactData.iata_code || null,
        });
      } else {
        setAirline(null);
      }
    } catch (err) {
      console.error(err);
      setAirline(null);
    }

    setLoading(false);
  };

  return (
    <div className="faq-page">
      {/* ========================================================
         ⭐ SEO BLOCK
      ========================================================= */}
      <Helmet>
        <title>
          Airline Contact Numbers – Heathrow Airline Directory | CheapCabGrab
        </title>

        <meta
          name="description"
          content="Find customer service numbers, reservation lines, lost property contacts, websites and special assistance details for all major airlines operating at Heathrow Airport."
        />

        <link
          rel="canonical"
          href="https://www.cheapcabgrab.com/airlineContacts"
        />

        <meta
          name="keywords"
          content="airline phone numbers, airline customer service, Heathrow airlines contact, lost property Heathrow, airline special assistance, airline helpline"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="Airline Contact Numbers – Heathrow Airline Directory"
        />
        <meta
          property="og:description"
          content="Search any airline to see reservation lines, customer service, lost property and special assistance contact details."
        />
        <meta
          property="og:url"
          content="https://www.cheapcabgrab.com/airlineContacts"
        />

        {/* JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "Airline Contact Directory",
            description:
              "Customer service phone numbers, reservation lines, lost property contact details, and support information for airlines flying from Heathrow Airport.",
          })}
        </script>
      </Helmet>

      {/* ========================================================
         ⭐ TWO-COLUMN GRID (like FAQ / TerminalFinder / Events)
      ========================================================= */}
      <div className="faq-grid">
        {/* ---------------- LEFT SIDE CONTENT ---------------- */}
        <div className="faq-left">
          <header className="faq-header">
            <h1>📞 Airline Contacts Directory</h1>
            <p>
              Search any airline to instantly find their reservation line, lost property
              desk, special assistance number and website.  
            </p>
          </header>

          {/* Search box */}
          <div style={{ marginBottom: "20px" }}>
            <input
              type="text"
              className="finder-modern-input"
              placeholder="Search airline (min 3 letters)…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <button className="finder-modern-btn" onClick={search}>
              Search
            </button>
          </div>

          {/* Validation */}
          {searched && query.length < 3 && (
            <p style={{ color: "#b91c1c", fontWeight: 600 }}>
              Please enter at least 3 characters.
            </p>
          )}

          {loading && <p>Loading…</p>}

          {/* ---------------------------------------------------
             RESULTS CARD
          ---------------------------------------------------- */}
          {searched && !loading && airline && (
            <div className="terminal-card-clean" style={{ marginTop: "10px" }}>
              <h3 style={{ marginBottom: "10px" }}>
                {airline.airline} ({airline.iata_code || "—"})
              </h3>

              <p><strong>Reservations:</strong> {airline.reservation_phone || "—"}</p>
              <p><strong>Customer Service:</strong> {airline.customer_service_phone || "—"}</p>
              <p><strong>Special Assistance:</strong> {airline.special_assistance_phone || "—"}</p>
              <p><strong>Lost Property:</strong> {airline.lost_property_phone || "—"}</p>
              <p><strong>Email:</strong> {airline.email || "—"}</p>

              {airline.website && (
                <p>
                  <strong>Website: </strong>
                  <a href={airline.website} target="_blank" rel="noopener noreferrer">
                    {airline.website}
                  </a>
                </p>
              )}

              <div className="terminal-clean-actions">
                <Link className="terminal-book-btn-clean" to="/heathrow-taxi">
                  🚖 Book Taxi
                </Link>

                <Link
                  className="terminal-contact-btn-clean"
                  to={`/terminalFinder?airline=${encodeURIComponent(
                    cleanAirlineName(airline.airline)
                  )}`}
                >
                  🛬 Check Terminal
                </Link>
              </div>
            </div>
          )}

          {/* No airline found */}
          {searched && !loading && !airline && query.length >= 3 && (
            <p>No airline found.</p>
          )}

          {/* Helpful quick links */}
          <section className="faq-section" style={{ marginTop: "40px" }}>
            <h2>✈️ Related Tools</h2>
            <ul>
              <li><Link className="faq-link" to="/terminalFinder">Heathrow Terminal Finder</Link></li>
              <li><Link className="faq-link" to="/heathrow-taxi">Heathrow Taxi Transfers</Link></li>
              <li><Link className="faq-link" to="/heathrow-hotels">Hotels Near Heathrow</Link></li>
              <li><Link className="faq-link" to="/heathrow-attractions">Heathrow Attractions</Link></li>
            </ul>
          </section>

          <div style={{ marginTop: "40px" }}>
            <Link to="/" className="event-footer-cta">
              🚖 Get an Instant Taxi Quote
            </Link>
          </div>
        </div>

        {/* ---------------- RIGHT-SIDE REVIEWS ---------------- */}
        <aside className="faq-right">
          <div className="faq-reviews-card">
            <h2>⭐ Customer Reviews</h2>
            <ReviewsBar />
          </div>
        </aside>
      </div>
    </div>
  );
}

